import React from 'react'
import type { Engagement, FfpDashboardData, PeriodKey } from '../../types'
import { formatPeople, formatUpdated, longMonth, peopleHave, plural, recentPhrase, sincePhrase, slashMonth } from '../../utils'
import { Card } from '../Card'
import styles from './engagement.module.sass'
import { Meaning, Message, Metric, Note, VisuallyHidden } from '../CardParts'
import { Figure, ShareBar, SmallPrint } from './parts'
import {
  cappedShare,
  isEmptyPeriod,
  workshopState,
  WorkshopState,
} from './utils'

type Workshops = Engagement['workshops']

type Props = {
  data: FfpDashboardData
  period: PeriodKey
  footer: string
  highlighted: boolean
  onAbout: () => void
}

export const WorkshopsCard = ({ data, period, footer, highlighted, onAbout }: Props) => {
  const { enrolled } = data.people
  const workshops = data.engagement.workshops
  const state = workshopState(workshops)
  // Workshops have their own start: the first one anyone attended, which the
  // badge and the workshops-held count also go back to.
  const since = sincePhrase(workshops.first_month)

  return (
    <Card
      question="Workshop attendance"
      title={workshopsTitle(state, workshops.attendees, since)}
      onAbout={onAbout}
      highlighted={highlighted}
      footer={{ text: footer, chip: 'Everyone enrolled included' }}
    >
      <Metric text={`Workshops · share of ${enrolled} enrolled people who attended at least one`} />
      <WorkshopsBody data={data} period={period} since={since} state={state} />
      <SmallPrint text={latestRecorded(data.freshness.workshops_imported_at)} />
      <Meaning text="Group sessions of about an hour on one topic, such as debt, budgeting or retirement. Run on site and online." />
    </Card>
  )
}

const workshopsTitle = (state: WorkshopState, attendees: number, since: string): string => {
  if (state === 'no_attendance') return 'Workshops'
  if (state === 'under_five') return `${peopleHave(attendees)} been to a workshop so far`
  return `Workshops ${since}`
}

// Planner can't tie a workshop to the company until its attendance is imported.
// The time is the latest attendance recorded, not the latest import, so it
// can't say whether anything is waiting.
const latestRecorded = (recordedAt: string | null): string | null => {
  const updated = formatUpdated(recordedAt)
  return updated ? `Latest attendance recorded ${updated}.` : null
}

type BodyProps = { data: FfpDashboardData; period: PeriodKey; since: string; state: WorkshopState }

const WorkshopsBody = ({ data, period, since, state }: BodyProps) => {
  if (state === 'no_attendance') {
    return <Message title="No workshop attendance yet" text="Attendance will show here a few days after the first workshop." />
  }

  const workshops = data.engagement.workshops
  const share = cappedShare(workshops.attendees, data.people.enrolled)
  const firstMonth = workshops.first_month

  return (
    <>
      <EmptyPeriodMessage workshops={workshops} period={period} since={since} />
      <AttendedFigure share={share} attendees={workshops.attendees} state={state} change={firstMonth ? `First held in ${longMonth(firstMonth)}` : null} />
      <ShareBar share={share} channel="workshops" />
      <Topics workshops={workshops} state={state} />
      <Note text={heldNote(workshops, period, since)} />
    </>
  )
}

// The card keeps its since-start figures when the period had no workshops.
const EmptyPeriodMessage = ({ workshops, period, since }: { workshops: Workshops; period: PeriodKey; since: string }) => {
  if (!isEmptyPeriod(workshops, period)) return null

  return (
    <Message
      title={`No workshops ${recentPhrase(period)} · ${workshops.sessions_held} held ${since}`}
      text="The figures below cover the whole programme."
    />
  )
}

type AttendedFigureProps = { share: number; attendees: number; state: WorkshopState; change: string | null }

const AttendedFigure = ({ share, attendees, state, change }: AttendedFigureProps) => {
  const label = state === 'under_five' ? `${peopleHave(attendees)} attended a workshop` : `${formatPeople(attendees)} attended at least one`
  return <Figure value={`${share}%`} label={label} change={change} />
}

// "9 workshops held since Feb 2026." or, for a recent period,
// "3 workshops held in the last 3 months, 9 since Feb 2026."
const heldNote = (workshops: Workshops, period: PeriodKey, since: string): string => {
  const total = `${plural(workshops.sessions_held, 'workshop')} held ${since}`

  const recent = recentPhrase(period)
  if (!recent || isEmptyPeriod(workshops, period)) return `${total}.`

  const inPeriod = plural(workshops.periods[period].sessions_held, 'workshop')
  return `${inPeriod} held ${recent}, ${workshops.sessions_held} ${since}.`
}

type TopicRowData = { key: string; name: string; month: string; attendees: number | null }

// The topics in the server's order, then the attendees whose workshop had no topic.
const topicRows = (workshops: Workshops): TopicRowData[] => {
  const rows = workshops.topics.map((topic) => ({
    key: `topic:${topic.name}`,
    name: topic.name,
    month: topic.latest_month ? slashMonth(topic.latest_month) : '',
    attendees: topic.hidden ? null : topic.attendees,
  }))
  if (workshops.untagged_attendees === 0) return rows

  // null when the server hides a group under the minimum.
  return rows.concat([{ key: 'untagged', name: 'Other topics', month: '', attendees: workshops.untagged_attendees }])
}

const Topics = ({ workshops, state }: { workshops: Workshops; state: WorkshopState }) => {
  const rows = topicRows(workshops)
  if (rows.length === 0) return null

  const most = Math.max(1, ...rows.map((row) => row.attendees ?? 0))
  const minimum = workshops.minimum_people
  const items = rows.map((row) => <TopicRow key={row.key} row={row} most={most} minimum={minimum} />)
  const note = state === 'under_five'
    ? `Workshops with fewer than ${minimum} people are hidden to protect anonymity.`
    : `Topics with fewer than ${minimum} people are hidden to protect anonymity. Other topics covers workshops without a set topic.`

  return (
    <div className={styles.block}>
      <p className={styles.blockTitle}>People at each workshop topic</p>
      <table className={styles.topics}>
        <thead className={styles.topicsHead}>
          <tr>
            <th scope="col" className={styles.topicsTh}>Topic</th>
            <th scope="col" className={`${styles.topicsTh} ${styles.topicsMonth}`}>Latest</th>
            <th scope="col" className={styles.topicsTh}><VisuallyHidden>Attendance</VisuallyHidden></th>
            <th scope="col" className={`${styles.topicsTh} ${styles.topicsCount}`}>People</th>
          </tr>
        </thead>
        <tbody>{items}</tbody>
      </table>
      <p className={styles.smallPrint}>{note}</p>
    </div>
  )
}

type TopicRowProps = { row: TopicRowData; most: number; minimum: number }

const TopicRow = ({ row, most, minimum }: TopicRowProps) => (
  <tr>
    <td className={styles.topicName}>{row.name}</td>
    <td className={styles.topicMonth}>{row.month}</td>
    <td className={styles.topicBarCell}>
      <TopicBar attendees={row.attendees} most={most} minimum={minimum} />
    </td>
    <td className={styles.topicCount}>{row.attendees ?? ''}</td>
  </tr>
)

const TopicBar = ({ attendees, most, minimum }: { attendees: number | null; most: number; minimum: number }) => {
  if (attendees === null) {
    return (
      <span className={styles.topicTrack}>
        <span className={styles.topicHidden}>{`Hidden: fewer than ${minimum}`}</span>
      </span>
    )
  }

  return (
    <span className={styles.topicTrack}>
      <span className={styles.topicFill} style={{ width: `${Math.round((attendees / most) * 100)}%` }} />
    </span>
  )
}
